import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { buildFavPlanPath, FAV_PLAN_FIRST_STEP_SLUG } from '../../../constants/myFavoritePlanForm';
import { TextsContext } from '../../../context/textsContext';
import { useCotizador } from '../../../context/apiContext';
import { TableComponent } from '../../../components/table';
import DashboardFilters from './DashboardFilters';
import DeletePlanDialog from './DeletePlanDialog';
import { useDashboardFilters } from '../../../hooks/myFavoritePlan/useDashboardFilters';
import { useFavoritePlans } from '../../../hooks/myFavoritePlan/useFavoritePlans';
import { useDeleteFavoritePlan } from '../../../hooks/myFavoritePlan/useDeleteFavoritePlan';
import { useDownloadFavoritePlanPremiumTable } from '../../../hooks/myFavoritePlan/useDownloadFavoritePlanPremiumTable';
import { getPlanFormState } from '../../../helpers/FavoritePlanToQuote';
import { isFavoritePlanStale } from '../../../helpers/FavoritePlanValidation';
import StaleFavoritePlanWarningModal from '../../../components/staleFavoritePlan/StaleFavoritePlanWarningModal';
import DialogComponent from '../../../components/DialogComponent';
import { TitleH2Component } from '../../../components/TitleH2Component';
import { ParagraphComponent } from '../../../components/ParagraphComponent';
import { quoter } from '../../../api/feature/quoterApi';
import style from '../../../scss/dashboard.module.scss';

const PAGE_SIZE = 5;

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Formatea a `Month/DD/YYYY` (p. ej. `June/05/2026`).
 *
 * Las fechas llegan como medianoche UTC (`2026-07-20T00:00:00.000Z`): formatearlas en la
 * zona local (UTC-5) caería al día anterior, así que se leen en UTC para que la fecha
 * mostrada sea la que el back guardó.
 *
 * `fallback` es lo que se pinta cuando no hay fecha: la fecha de creación usa '—', pero la
 * de actualización debe quedar en blanco si el template nunca se editó.
 */
const formatDate = (value, fallback) => {
    if (!value) return fallback;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const month = MONTH_NAMES[date.getUTCMonth()];
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${month}/${day}/${date.getUTCFullYear()}`;
};

const EMPTY_VALUE = '—';

/**
 * Códigos de beneficio dentro de `data.selectedBenefitsNames`, donde cada valor ya viene
 * formateado para mostrar (p. ej. `maxcovpipoly: '$4,000,000.00'`).
 */
const BENEFIT_CODES = {
    MAX_COVERAGE: 'maxcovpipoly',
    GEOGRAPHICAL_COVERAGE: 'geogrcov',
    PROVIDER_NETWORK: 'provdnet',
    DEDUCTIBLE_REDUCTION: 'deductbl',
    MATERNITY: 'matrnity',
};

const YES_NO_LABEL = (value, texts) => (value
    ? (texts?.general_form_option_yes ?? 'Yes')
    : (texts?.general_form_option_no ?? 'No'));

/**
 * Maps a favorite plan coming from the API into the row shape the table columns read.
 *
 * Nombre y fechas salen del item de la API; el resto del estado del formulario guardado
 * (`plan.data.contextData`). Las columnas Sí/No de outpatient, preventive checkup y
 * maternity reflejan directamente los toggles que marcó el agente (`outpationBenefits*`,
 * `step14Confirmation`), no el valor de cobertura elegido. Se lee de forma defensiva:
 * un plan sin cierto beneficio simplemente pinta "—" en esa columna.
 */
const mapPlanToRow = (plan, texts) => {
    const quote = getPlanFormState(plan.data);
    const benefits = quote.selectedBenefitsNames ?? {};
    const selected = quote.selectedBenefits ?? {};
    const benefitValue = (code) => benefits[code] || EMPTY_VALUE;

    // El form de templates (`BaseInformationStep`) sólo captura el toggle "¿Necesita agregar
    // un dependiente?" (`addDependant`); nunca llena `dependants`, que queda en `[]`. Así que
    // ese toggle es la única señal fiable de si el template lleva dependientes.
    const hasDependents = Boolean(quote.addDependant);

    // El género se guarda como '1' (male) / '2' (female), igual que en el formulario.
    // eslint-disable-next-line eqeqeq
    const gender = quote.gender == 1 ? (texts?.general_form_option_male ?? 'Male')
        // eslint-disable-next-line eqeqeq
        : quote.gender == 2 ? (texts?.general_form_option_female ?? 'Female')
            : EMPTY_VALUE;

    // La reducción de deducible no guarda nombre para mostrar, sólo el código
    // 'deductbl_yes' / 'deductbl_no' en `selectedBenefits`.
    const deductibleReductionCode = selected[BENEFIT_CODES.DEDUCTIBLE_REDUCTION];
    const deductibleReduction = deductibleReductionCode
        ? YES_NO_LABEL(deductibleReductionCode === 'deductbl_yes', texts)
        : EMPTY_VALUE;

    return {
        id: plan.id ?? plan._id,
        // El payload guardado entero, que es lo que necesita la acción "use" para precargar
        // el cotizador (`mapFavoritePlanToQuoteContext` lo desenvuelve): el resto de campos
        // de la fila ya vienen formateados para mostrar (p. ej. '$4,000,000.00') y no
        // sirven como estado.
        planData: plan.data,
        planName: plan.description ?? EMPTY_VALUE,
        createdDate: formatDate(plan.createdDate, EMPTY_VALUE),
        // `lastUpdatedAt` viene null en los planes recién creados: queda en blanco hasta
        // que el template se edita por primera vez.
        updatedDate: formatDate(plan.lastUpdatedAt, ''),
        dependents: YES_NO_LABEL(hasDependents, texts),
        gender,
        maxCoverage: benefitValue(BENEFIT_CODES.MAX_COVERAGE),
        graphicalCoverage: benefitValue(BENEFIT_CODES.GEOGRAPHICAL_COVERAGE),
        providerNetwork: benefitValue(BENEFIT_CODES.PROVIDER_NETWORK),
        // Toggle "¿Quiere beneficios ambulatorios?" del paso de Outpatient Benefits.
        outpatientBenefits: YES_NO_LABEL(Boolean(quote.outpationBenefitsEnabled), texts),
        deductibleReduction,
        // Toggle del rider de preventive health checkup (`healthch`).
        preventiveCheckup: YES_NO_LABEL(Boolean(quote.step14Confirmation), texts),
        // Toggle del rider de maternidad (`matrnity`).
        maternityComplications: YES_NO_LABEL(Boolean(quote.outpationBenefitsmaternidad), texts),
        // Cobertura máxima de maternidad/recién nacido elegida en el rider.
        newbornMaternity: benefitValue(BENEFIT_CODES.MATERNITY),
        totalTimesUsed: plan.usedCount ?? 0,
    };
};

const TableSection = () => {
    const { texts } = useContext(TextsContext);
    const navigate = useNavigate();
    const { applyFavoritePlan } = useCotizador();
    const { filters, page, setFilter, setPage, resetFilters, hasActiveFilters } = useDashboardFilters();

    // `isLoading` (not `isLoading`) so the skeleton shows on every request, including
    // background refetches from filter/pagination changes where cached data is kept.
    const { data: response, isLoading } = useFavoritePlans({ ...filters, page, limit: PAGE_SIZE });

    // The hook returns `data.data`, i.e. { data: [...plans], pagination: {...} }.
    const data = useMemo(
        () => (response?.data ?? []).map(plan => mapPlanToRow(plan, texts)),
        [response, texts]
    );

    const totalPages = response?.pagination?.totalPages ?? 1;

    // Plan pending deletion (drives the confirmation dialog). `null` => dialog closed.
    const [planToDelete, setPlanToDelete] = useState(null);

    // Plan que resultó no cotizable al revalidarlo antes de usarlo (drives the
    // stale-benefits warning). `false` => modal cerrado.
    const [staleWarning, setStaleWarning] = useState(false);

    // Fila para la que se está revalidando el plan antes de usarlo (ver handleAction).
    // `null` => ninguna fila en ese estado.
    const [validatingId, setValidatingId] = useState(null);

    // Sin onSuccess que cierre: el diálogo se queda abierto para mostrar el estado
    // de éxito (se auto-cierra a los 5s desde DeletePlanDialog).
    const deleteMutation = useDeleteFavoritePlan();

    // Descarga del PDF de la premium table. Una sola instancia para toda la tabla:
    // mientras corre, `variables.id` dice qué fila se está descargando.
    const downloadPremiumTableMutation = useDownloadFavoritePlanPremiumTable();
    const downloadingId = downloadPremiumTableMutation.isLoading
        ? downloadPremiumTableMutation.variables?.id
        : null;

    // Fila para la que se está recotizando (paso previo a la descarga, ver handleAction).
    // `null` => ninguna fila en ese estado.
    const [computingId, setComputingId] = useState(null);

    // Aviso de que el recotizado previo a la descarga falló (ver handleAction).
    const [downloadError, setDownloadError] = useState(false);

    const handleAction = useCallback((action, row) => {
        if (action === 'delete') {
            setPlanToDelete(row);
            return;
        }
        if (action === 'edit') {
            // El formulario se precarga solo a partir del id de la URL.
            navigate(buildFavPlanPath(FAV_PLAN_FIRST_STEP_SLUG, row.id));
            return;
        }
        if (action === 'download') {
            // No se re-dispara si esa fila ya se está recotizando o descargando.
            if (downloadPremiumTableMutation.isLoading || computingId) return;

            // El template puede haber quedado desactualizado (tarifas, beneficios) desde
            // que se guardó, así que se recotiza contra `/rate-engine/compute` antes de
            // pedir el PDF: si eso falla, se avisa en vez de arrastrar precios obsoletos
            // (o de dejar que el back falle al armar el documento).
            setComputingId(row.id);
            quoter(row.planData)
                .then((response) => {
                    if (!response || response?.data == null) {
                        throw new Error('compute_failed');
                    }
                    downloadPremiumTableMutation.mutate({ id: row.id, planName: row.planName });
                })
                .catch(() => {
                    setDownloadError(true);
                })
                .finally(() => setComputingId(null));
            return;
        }
        if (action === 'use') {
            // No se re-dispara si esa fila ya se está revalidando.
            if (validatingId) return;

            // El ApiProvider envuelve toda la app (AppWrapper), así que el contexto del
            // cotizador se puede precargar en cuanto se confirma que el plan sigue
            // siendo válido. Antes de eso se revalida contra `/rate-engine/compute`
            // (ver helpers/FavoritePlanValidation.js): un beneficio guardado que ya no
            // exista hace fallar el compute con 400, y en vez de cargarlo igual se
            // avisa y no se navega.
            setValidatingId(row.id);
            isFavoritePlanStale(row.planData)
                .then((isStale) => {
                    if (isStale) {
                        setStaleWarning(true);
                        return;
                    }
                    applyFavoritePlan(row.planData, row.id);
                    navigate('/step1');
                })
                .finally(() => setValidatingId(null));
            return;
        }
        // eslint-disable-next-line no-console
    }, [navigate, applyFavoritePlan, downloadPremiumTableMutation, computingId, validatingId]);

    const closeStaleWarning = useCallback(() => setStaleWarning(false), []);

    const closeDeleteDialog = useCallback(() => {
        if (deleteMutation.isLoading) return;
        setPlanToDelete(null);
        // Limpia un posible estado de error para que reabrir el diálogo arranque limpio.
        deleteMutation.reset();
    }, [deleteMutation]);

    const confirmDelete = useCallback(() => {
        if (!planToDelete) return;
        deleteMutation.mutate(planToDelete.id);
    }, [planToDelete, deleteMutation]);

    const columns = useMemo(() => [
        { key: 'planName', label: texts?.dashboard_table_plan_name ?? 'Plan Name', align: 'left', cellClassName: 'fw_700', headerClassName: 'fw_700' },
        { key: 'createdDate', label: texts?.dashboard_table_created_date ?? 'Created Date' },
        { key: 'updatedDate', label: texts?.dashboard_table_updated_date ?? 'Updated Date' },
        { key: 'dependents', label: texts?.dashboard_table_dependents ?? 'Dependents' },
        { key: 'gender', label: texts?.dashboard_table_gender ?? 'Gender' },
        { key: 'maxCoverage', label: texts?.dashboard_table_maximum_coverage ?? 'Maximum Coverage' },
        { key: 'graphicalCoverage', label: texts?.dashboard_table_graphical_coverage ?? 'Geographical coverage' },
        { key: 'providerNetwork', label: texts?.dashboard_table_provider_network ?? 'Provider network' },
        { key: 'outpatientBenefits', label: texts?.dashboard_table_outpatient_benefits ?? 'Outpatient benefits' },
        { key: 'deductibleReduction', label: texts?.dashboard_table_deductible_reduction ?? 'Deductible Reduction' },
        { key: 'preventiveCheckup', label: texts?.dashboard_table_preventive_checkup ?? 'Preventative health check-ups' },
        { key: 'maternityComplications', label: texts?.dashboard_table_maternity_complications ?? 'Maternity complications benefits' },
        { key: 'newbornMaternity', label: texts?.dashboard_table_newborn_maternity ?? 'Newborn and maternity complications' },
        {
            key: 'totalTimesUsed',
            label: texts?.dashboard_table_total_times_used ?? 'Total Times Used',
            sticky: true,
            width: 160,
            headerColor: '#243D71',
            headerTextColor: '#fff',
            cellColor: '#dfe3ec',
        },
        {
            key: 'actions',
            label: texts?.dashboard_table_actions ?? 'Actions',
            sticky: true,
            width: 190,
            cellColor: '#fff',
            render: (_value, row) => {
                const isDownloading = downloadingId === row.id || computingId === row.id;
                const isValidating = validatingId === row.id;
                const useLabel = texts?.dashboard_action_use ?? 'Use plan';
                const editLabel = texts?.dashboard_action_edit ?? 'Edit plan';
                const downloadLabel = texts?.dashboard_action_download ?? texts?.dashboard_table_download_premium_table ?? 'Download premium table';
                const deleteLabel = texts?.dashboard_action_delete ?? 'Delete plan';
                // El tooltip se porta a document.body para que no lo recorte el
                // overflow del contenedor con scroll de la tabla.
                const withTooltip = (id, label, button) => (
                    <OverlayTrigger
                    
                        placement="top"
                        container={document.body}
                        overlay={<Tooltip id={id} className="custom-tooltip">{label}</Tooltip>}
                    >
                        {button}
                    </OverlayTrigger>
                );
                return (
                    <div className={style.actions}>
                        {withTooltip(`tt-use-${row.id}`, useLabel, (
                            <button
                                type="button"
                                className={`${style.actionBtn} ${style.actionAdd}`}
                                onClick={() => handleAction('use', row)}
                                disabled={isValidating}
                                aria-label={useLabel}
                            >
                                <Icon icon={isValidating ? 'mdi:loading' : 'mdi:clipboard-plus-outline'} className={isValidating ? style.actionSpin : ''} />
                            </button>
                        ))}
                        {withTooltip(`tt-edit-${row.id}`, editLabel, (
                            <button type="button" className={`${style.actionBtn} ${style.actionEdit}`} onClick={() => handleAction('edit', row)} aria-label={editLabel}>
                                <Icon icon="mdi:pencil-box-outline" />
                            </button>
                        ))}
                        {withTooltip(`tt-download-${row.id}`, downloadLabel, (
                            <button
                                type="button"
                                className={`${style.actionBtn} ${style.actionDownload}`}
                                onClick={() => handleAction('download', row)}
                                disabled={isDownloading}
                                aria-label={downloadLabel}
                            >
                                <Icon icon={isDownloading ? 'mdi:loading' : 'mdi:file-download-outline'} className={isDownloading ? style.actionSpin : ''} />
                            </button>
                        ))}
                        {withTooltip(`tt-delete-${row.id}`, deleteLabel, (
                            <button type="button" className={`${style.actionBtn} ${style.actionDelete}`} onClick={() => handleAction('delete', row)} aria-label={deleteLabel}>
                                <Icon icon="mdi:trash-can-outline" />
                            </button>
                        ))}
                    </div>
                );
            },
        },
    ], [texts, handleAction, downloadingId, computingId, validatingId]);

    return (
        <div className={style.card}>
            <DashboardFilters
                filters={filters}
                setFilter={setFilter}
                resetFilters={resetFilters}
                hasActiveFilters={hasActiveFilters}
            />

            <TableComponent
                columns={columns}
                data={data}
                loading={isLoading}
                skeletonRows={PAGE_SIZE}
                rowKey="id"
                emptyState={{
                    icon: 'lucide:inbox',
                    title: texts?.dashboard_empty_title ?? "You don't have any favorite plans yet.",
                    text: texts?.dashboard_empty_text ?? 'Create your first one!',
                }}
                pagination={{
                    page,
                    totalPages,
                    onPageChange: setPage,
                    label: texts?.dashboard_pagination_label ?? 'Page',
                }}
            />

            <DeletePlanDialog
                show={!!planToDelete}
                isLoading={deleteMutation.isLoading}
                isError={deleteMutation.isError}
                isSuccess={deleteMutation.isSuccess}
                onConfirm={confirmDelete}
                onClose={closeDeleteDialog}
            />

            <StaleFavoritePlanWarningModal
                show={staleWarning}
                onConfirm={closeStaleWarning}
                confirmText={texts?.fav_plan_stale_benefits_acknowledge ?? 'OK'}
            />

            <DialogComponent
                show={downloadError}
                onConfirm={() => setDownloadError(false)}
                confirmText={texts?.modal_button_ok ?? 'OK'}
            >
                <Icon icon="mdi:alert-circle-outline" width={40} height={40} style={{ color: '#F12B24' }} className="mb-3" />
                <TitleH2Component styleGroup="raleway fw_700 color_primary mb-2">
                    {texts?.dashboard_download_error_title ?? 'Download failed'}
                </TitleH2Component>
                <ParagraphComponent styleGroup="Poppins color_primary mb-0">
                    {texts?.dashboard_download_error_text ?? 'It is not possible to download the document. Please try again.'}
                </ParagraphComponent>
            </DialogComponent>
        </div>
    );
};

export default React.memo(TableSection);
